import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Map, Grid, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import PropertyCard from '../components/PropertyCard';
import PropertyMap from '../components/PropertyMap';
import PropertyMapView from '../components/PropertyMapView';
import CategoryFilter from '../components/CategoryFilter';
import DatePicker from '../components/DatePicker';
import GuestPicker from '../components/GuestPicker';
import BedroomPicker from '../components/BedroomPicker';
import MapboxMap from '../components/MapboxMap';
import { propertiesAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { LocalBusinessSchema, OrganizationSchema } from '../components/SchemaMarkup';
import ExperienceMontrealCarousel from '../components/home/ExperienceMontrealCarousel';

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchLocation, setSearchLocation] = useState('');
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guests, setGuests] = useState({ adults: 1, children: 0 });
  const [bedrooms, setBedrooms] = useState(null); // New: bedrooms filter
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false); // Changed to false - no initial load
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Restore view mode from sessionStorage
    return sessionStorage.getItem('homeViewMode') || 'grid';
  }); // 'grid' or 'map'
  const [hasSearched, setHasSearched] = useState(false); // New: track if user has searched
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  
  // Map dialog states
  const [stDenisMapOpen, setStDenisMapOpen] = useState(false);
  // Note: the Decarie map dialog state was removed when we switched to
  // city-level grouping. The St-Denis state is reused for the Montreal
  // map dialog. Map zoom/center is set to the city level (zoom 11) in
  // the dialog body so it shows the whole metro area.

  // Check if user came from WiFi access (for future targeted marketing)
  const [fromWiFi, setFromWiFi] = useState(false);
  const [building, setBuilding] = useState(null);

  // Show the "create an account" banner only when the visitor isn't
  // already signed in. We check on mount + every time localStorage
  // changes (storage event covers another tab signing in/out).
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    Boolean(
      typeof window !== 'undefined' &&
        (localStorage.getItem('user_token') || localStorage.getItem('token')),
    ),
  );
  useEffect(() => {
    const sync = () =>
      setIsLoggedIn(
        Boolean(
          localStorage.getItem('user_token') || localStorage.getItem('token'),
        ),
      );
    window.addEventListener('storage', sync);
    // Also re-check on focus (covers same-tab login flows that don't fire storage).
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  // Save view mode to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('homeViewMode', viewMode);
  }, [viewMode]);

  // Reset homepage to browsing state (show Browse by Location, not full welcome page)
  const resetHomePage = () => {
    setSearchLocation('');
    setCheckInDate(null);
    setCheckOutDate(null);
    setGuests({ adults: 1, children: 0 });
    setBedrooms(null);
    setSelectedCategory('all');
    setProperties([]);
    setHasSearched(false); // Show Browse by Location sections
    setError(null);
    setViewMode('grid');
    // Clear sessionStorage
    sessionStorage.removeItem('homeViewMode');
  };

  // Listen for navigation events to reset when logo is clicked
  useEffect(() => {
    // Check for reset URL parameter
    const params = new URLSearchParams(window.location.search);
    const shouldReset = params.get('reset');
    
    if (shouldReset === 'true' || location.state?.reset) {
      resetHomePage();
      // Clear the URL parameter
      window.history.replaceState({}, document.title, '/');
    }
  }, [location]);

  // Don't fetch properties on mount - wait for user to search
  useEffect(() => {
    // Removed: fetchProperties();
    
    // Check URL parameters for WiFi redirect
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const buildingParam = params.get('building');
    
    if (source === 'wifi' && buildingParam) {
      setFromWiFi(true);
      setBuilding(buildingParam);
      
      // Show welcome message
      toast({
        title: `Welcome back from ${buildingParam}! 🏠`,
        description: 'Browse our properties and save on your next booking',
      });
    }
  }, [selectedCategory]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertiesAPI.getAll({ category: selectedCategory });
      setProperties(data);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('Failed to load properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    
    // If user selects 'all', reset to initial homepage state (show welcome sections)
    if (category === 'all') {
      setHasSearched(false);
      setProperties([]);
      setSearchLocation('');
      setCheckInDate(null);
      setCheckOutDate(null);
      setGuests({ adults: 1, children: 0 });
      setBedrooms(null);
    } else {
      // For specific categories, trigger search and show filtered results
      setHasSearched(true);
      fetchProperties();
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setHasSearched(true); // Mark that user has performed a search
      const totalGuests = guests.adults + guests.children;
      
      // If dates are selected, first get available property IDs
      let availablePropertyIds = null;
      if (checkInDate && checkOutDate) {
        // Validate dates
        if (checkOutDate <= checkInDate) {
          toast({
            title: 'Invalid dates',
            description: 'Check-out date must be after check-in date',
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }
        
        try {
          const availabilityResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/availability/search?check_in=${checkInDate.toISOString()}&check_out=${checkOutDate.toISOString()}`
          );
          
          if (!availabilityResponse.ok) {
            throw new Error(`HTTP error! status: ${availabilityResponse.status}`);
          }
          
          const availabilityData = await availabilityResponse.json();
          availablePropertyIds = availabilityData.available_property_ids;
          
          console.log(`Found ${availablePropertyIds.length} available properties for selected dates`);
          console.log('Available property IDs:', availablePropertyIds);
        } catch (err) {
          console.error('Error checking availability:', err);
          toast({
            title: 'Warning',
            description: 'Could not filter by availability. Showing all properties.',
            variant: 'destructive'
          });
        }
      }
      
      // Fetch all properties with filters
      const data = await propertiesAPI.getAll({ 
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        location: searchLocation || undefined,
        guests: totalGuests > 1 ? totalGuests : undefined,
        bedrooms: bedrooms !== null ? bedrooms : undefined
      });
      
      // Filter by availability if dates were selected
      let filteredProperties = data;
      if (availablePropertyIds !== null && checkInDate && checkOutDate) {
        filteredProperties = data.filter(property => {
          // Check both _id and id fields to be safe
          const propertyId = property._id || property.id;
          const isAvailable = availablePropertyIds.includes(propertyId);
          console.log(`Property ${property.title} (${propertyId}): ${isAvailable ? 'AVAILABLE' : 'BOOKED'}`);
          return isAvailable;
        });
        
        if (filteredProperties.length === 0) {
          toast({
            title: 'No available properties',
            description: 'No properties available for the selected dates. Try different dates.',
            variant: 'destructive'
          });
        }
      }
      
      setProperties(filteredProperties);
      
      // Show search summary
      const searchDetails = [];
      if (searchLocation) searchDetails.push(searchLocation);
      if (checkInDate) searchDetails.push(`from ${checkInDate.toLocaleDateString()}`);
      if (checkOutDate) searchDetails.push(`to ${checkOutDate.toLocaleDateString()}`);
      if (totalGuests > 1) searchDetails.push(`${totalGuests} guests`);
      
      if (searchDetails.length > 0) {
        toast({
          title: 'Search updated',
          description: `${filteredProperties.length} available propert${filteredProperties.length !== 1 ? 'ies' : 'y'} ${searchDetails.join(', ')}`,
        });
      }
    } catch (err) {
      console.error('Error searching properties:', err);
      setError('Failed to search properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={t('home.seoTitle')}
        description={t('home.seoDescription')}
        keywords="Montreal short-term rentals, Montreal apartments, Montreal vacation rentals, rent apartment Montreal, Montreal accommodation"
        url="https://homesbyli.com/search"
      />
      <OrganizationSchema />
      <LocalBusinessSchema />

      {/* Anonymous-visitor reminder: ID-verified accounts are required
          before booking. Renders only when no auth token is present —
          authenticated users (customer / host / admin) never see this. */}
      {!isLoggedIn && (
        <div
          className="border-b"
          style={{ backgroundColor: '#FDF6E9', borderBottomColor: '#F1E6D0' }}
          data-testid="signup-reminder-banner"
        >
          <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20 py-2.5 sm:py-3 flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <span
                className="hidden sm:flex w-8 h-8 rounded-full items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(31, 92, 66, 0.10)' }}
              >
                <UserPlus className="w-4 h-4" style={{ color: '#1F5C42' }} />
              </span>
              <p className="text-xs sm:text-sm text-gray-800 leading-snug min-w-0">
                <span className="font-semibold text-gray-900">Create an account</span>
                <span className="text-gray-700"> before making your first booking — we verify every guest with ID for everyone&apos;s safety.</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-xs sm:text-sm font-semibold text-white rounded-full px-3 sm:px-4 py-1.5 transition-colors whitespace-nowrap hover:opacity-90"
                style={{ backgroundColor: '#1E3F70' }}
                data-testid="signup-reminder-cta"
              >
                Sign up
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="hidden sm:inline-block text-xs sm:text-sm font-medium underline underline-offset-4 whitespace-nowrap"
                style={{ color: '#1F5C42' }}
                data-testid="signup-reminder-login"
              >
                Log in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Search Section */}
      <div className="border-b border-gray-200">
        <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20 py-3 sm:py-6">
          {/* Slogan above the search */}
          <div className="text-center mb-4 sm:mb-6" data-testid="homepage-slogan">
            <h1
              className="font-bold tracking-tight"
              style={{
                color: '#1F5C42',
                fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
                lineHeight: 1.1,
              }}
            >
              Your next adventure starts here
            </h1>
          </div>

          {/* Mobile compact search trigger */}
          <button
            type="button"
            onClick={() => setShowMobileSearch(true)}
            className="md:hidden w-full flex items-center gap-3 bg-white border border-gray-300 rounded-full shadow-md px-4 py-3 text-left hover:shadow-lg transition-shadow"
            data-testid="mobile-search-trigger"
          >
            <Search className="w-4 h-4 text-gray-700 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {searchLocation || t('search.where', 'Where')}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {t('search.anyWeek', 'Any week')} · {t('search.addGuests', 'Add guests')}
              </div>
            </div>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#1F5C42' }}
            >
              <Search className="w-3.5 h-3.5 text-white" />
            </div>
          </button>

          {/* Desktop full search bar */}
          <div className="hidden md:flex items-center justify-center">
            <div className="w-full max-w-4xl">
              <div className="bg-white border border-gray-300 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center divide-x divide-gray-300">
                  {/* Location Search */}
                  <div className="flex-1 basis-0 min-w-0 px-6 py-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <label className="text-xs font-semibold text-gray-800 block mb-1">
                          {t('search.where')}
                        </label>
                        <Input
                          placeholder={t('search.searchPlaceholder')}
                          value={searchLocation}
                          onChange={(e) => setSearchLocation(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          className="border-0 p-0 h-6 text-sm placeholder:text-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Check In */}
                  <div className="flex-1 basis-0 min-w-0">
                    <DatePicker
                      date={checkInDate}
                      onDateChange={setCheckInDate}
                      placeholder={t('search.checkIn')}
                    />
                  </div>

                  {/* Check Out */}
                  <div className="flex-1 basis-0 min-w-0">
                    <DatePicker
                      date={checkOutDate}
                      onDateChange={setCheckOutDate}
                      placeholder={t('search.checkOut')}
                      minDate={checkInDate}
                    />
                  </div>

                  {/* Bedrooms */}
                  <div className="flex-1 basis-0 min-w-0">
                    <BedroomPicker
                      bedrooms={bedrooms}
                      onBedroomsChange={setBedrooms}
                    />
                  </div>

                  {/* Guests */}
                  <div className="flex-1 basis-0 min-w-0">
                    <GuestPicker
                      guests={guests}
                      onGuestsChange={setGuests}
                    />
                  </div>

                  {/* Search Button */}
                  <div className="pr-2 pl-4 flex-shrink-0">
                    <Button
                      onClick={handleSearch}
                      className="text-white rounded-full h-12 w-12 p-0 transition-all duration-200 hover:scale-105 hover:opacity-90"
                      style={{ backgroundColor: '#1F5C42' }}
                    >
                      <Search className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Sheet — see PropertyDetailPage for the modal={false}
          rationale (DatePicker/GuestPicker popovers must remain interactive). */}
      <Sheet open={showMobileSearch} onOpenChange={setShowMobileSearch} modal={false}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto p-0 rounded-t-2xl" data-testid="mobile-search-sheet">
          <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-white z-10">
            <SheetTitle className="text-base">{t('search.where', 'Where to?')}</SheetTitle>
          </SheetHeader>
          <div className="p-4 space-y-3">
            <div className="border border-gray-200 rounded-2xl px-4 py-3">
              <label className="text-xs font-semibold text-gray-800 block mb-1">
                {t('search.where')}
              </label>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-600" />
                <Input
                  placeholder={t('search.searchPlaceholder')}
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (setShowMobileSearch(false), handleSearch())}
                  className="border-0 p-0 h-7 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="mobile-search-location-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <DatePicker date={checkInDate} onDateChange={setCheckInDate} placeholder={t('search.checkIn')} />
              </div>
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <DatePicker date={checkOutDate} onDateChange={setCheckOutDate} placeholder={t('search.checkOut')} minDate={checkInDate} />
              </div>
            </div>
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <BedroomPicker bedrooms={bedrooms} onBedroomsChange={setBedrooms} />
            </div>
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <GuestPicker guests={guests} onGuestsChange={setGuests} />
            </div>
            <Button
              onClick={() => { setShowMobileSearch(false); handleSearch(); }}
              className="w-full text-white rounded-full h-12 font-semibold hover:opacity-90"
              style={{ backgroundColor: '#1F5C42' }}
              data-testid="mobile-search-submit"
            >
              <Search className="w-4 h-4 mr-2" />
              {t('search.search', 'Search')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Show Browse by Location and Target Audience sections only when user hasn't searched */}
      {!hasSearched && (
        <>
      {/* Browse by Location Section */}
      <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20 py-6 sm:py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('home.browseByLocation')}
          </h2>
          <p className="text-gray-600">
            {t('home.browseByLocationDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* City: Montreal — aggregates every neighborhood (St-Denis,
              Decarie, and any future hosts). We deliberately stopped
              splitting by neighborhood on the home page so new hosts
              don't feel left out / forced into a hyper-local bucket. */}
          <div className="group relative h-80 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
            <img
              src="https://images.unsplash.com/photo-1577872133778-fd7638801be0?w=1600&q=80&auto=format&fit=crop"
              alt="Montreal"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h3 className="text-3xl font-bold mb-2">Montreal</h3>
              <p className="text-lg text-white/90 mb-3">
                {t('home.montrealCityDesc', 'Stays across every Montreal neighborhood — Plateau, Decarie, downtown & more')}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Dialog open={stDenisMapOpen} onOpenChange={setStDenisMapOpen}>
                  <DialogTrigger asChild>
                    <button className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 hover:bg-white/30 transition-colors cursor-pointer">
                      <span>📍</span>
                      <span className="text-sm">Quebec, Canada</span>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                      <DialogTitle>Montreal — Greater Metropolitan Area</DialogTitle>
                    </DialogHeader>
                    {stDenisMapOpen && (
                      <div className="mt-4">
                        <MapboxMap
                          longitude={-73.5673}
                          latitude={45.5017}
                          zoom={11}
                          markerTitle="Montreal"
                          markerDescription="Quebec, Canada"
                          height="500px"
                        />
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/location/montreal');
                  }}
                  className="backdrop-blur-sm px-6 py-2 rounded-full font-semibold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl hover:opacity-90"
                  style={{ backgroundColor: '#1F5C42', color: '#fff' }}
                  data-testid="city-card-montreal"
                >
                  <span>{t('home.viewProperties')}</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>

          {/* Future cities — invitation-style card. As Homesbyli grows
              beyond Montreal we'll surface new city cards here. For now
              this doubles as a host-recruitment slot: hosts in other
              cities can apply to be the first listing. */}
          <div className="group relative h-80 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1449844908441-8829872d2607?w=800')] bg-cover bg-center opacity-25"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent"></div>

            {/* Coming-soon badge — top-right, gold, matches the rest of the site */}
            <span
              className="absolute top-4 right-4 z-10 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg"
              style={{ backgroundColor: '#FFC93C', color: '#3A2A00' }}
            >
              Coming soon
            </span>

            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h3 className="text-3xl font-bold mb-2">More cities</h3>
              <p className="text-lg text-white/90 mb-3">
                {t('home.moreCitiesDesc', 'New cities are being added as we welcome more verified hosts. Host elsewhere in Canada? Be the first listing in your city.')}
              </p>
              <div className="flex items-center gap-3 flex-wrap relative" style={{ zIndex: 999, position: 'relative' }}>
                <button
                  onClick={() => navigate('/location/all')}
                  className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 hover:bg-white/30 transition-all cursor-pointer"
                  style={{ zIndex: 1000, position: 'relative', pointerEvents: 'auto' }}
                  data-testid="city-card-browse-all"
                >
                  <span>🌍</span>
                  <span className="text-sm">{t('home.browseAll')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Experience Montreal — per-category partner carousels */}
      <ExperienceMontrealCarousel />

      {/* Gift cards banner — REMOVED 2026-07-02.
          Gift cards live on the UCWonder parent brand, not Homesbyli.
          Featuring a gift-card banner on the stays home page framed
          Homesbyli as "also a gift-card marketplace", which diluted the
          stays brand. Discovery of gift cards is now handled via the
          UCWonder cross-brand promo elsewhere on the site. */}

      {/* Target Audience Categories */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-b border-gray-200 py-12">
        <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('home.targetAudience')}</h2>
            <p className="text-gray-600">{t('home.targetAudienceDesc')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Students Card — Cobalt Navy */}
            <div
              onClick={() => navigate('/students')}
              className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-200"
              style={{ borderColor: '#E5E7EB' }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1E3F70')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
            >
              <div className="p-8 text-white" style={{ background: 'linear-gradient(135deg, #1E3F70, #3158A3)' }}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">{t('home.students')}</h3>
                <p className="text-white/80 text-sm">{t('home.studentsDesc')}</p>
              </div>
              <div className="p-6">
                {/* Exclusive Deal Badge */}
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-3 py-2 rounded-lg mb-4 text-center shadow-md">
                  <p className="text-xs font-bold">⭐ {t('home.exclusiveDeals')}</p>
                  <p className="text-xs">{t('home.studentsDeal')}</p>
                </div>

                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1E3F70' }}>✓</span>
                    <span>{t('home.studentsPoint1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1E3F70' }}>✓</span>
                    <span>{t('home.studentsPoint2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1E3F70' }}>✓</span>
                    <span>{t('home.studentsPoint3')}</span>
                  </li>
                </ul>
                <div
                  className="font-semibold group-hover:translate-x-2 transition-transform inline-flex items-center"
                  style={{ color: '#1E3F70' }}
                >
                  {t('home.browseStudents')}
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Medical Card — Deep Pine */}
            <div
              onClick={() => navigate('/medical')}
              className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-200"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1F5C42')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
            >
              <div className="p-8 text-white" style={{ background: 'linear-gradient(135deg, #1F5C42, #327A5A)' }}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2c0 1.1.9 2 2 2h5v5c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2h-2z"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">{t('home.medical')}</h3>
                <p className="text-white/80 text-sm">{t('home.medicalDesc')}</p>
              </div>
              <div className="p-6">
                {/* Exclusive Deal Badge */}
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-3 py-2 rounded-lg mb-4 text-center shadow-md">
                  <p className="text-xs font-bold">⭐ {t('home.exclusiveDeals')}</p>
                  <p className="text-xs">{t('home.medicalDeal')}</p>
                </div>

                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1F5C42' }}>✓</span>
                    <span>{t('home.medicalPoint1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1F5C42' }}>✓</span>
                    <span>{t('home.medicalPoint2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#1F5C42' }}>✓</span>
                    <span>{t('home.medicalPoint3')}</span>
                  </li>
                </ul>
                <div
                  className="font-semibold group-hover:translate-x-2 transition-transform inline-flex items-center"
                  style={{ color: '#1F5C42' }}
                >
                  {t('home.browseMedical')}
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Insurance Card — Fruity Pink */}
            <div
              onClick={() => navigate('/insurance')}
              className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-200"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#E83E70')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
            >
              <div className="p-8 text-white" style={{ background: 'linear-gradient(135deg, #E83E70, #FF5C8A)' }}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">{t('home.insurance')}</h3>
                <p className="text-white/80 text-sm">{t('home.insuranceDesc')}</p>
              </div>
              <div className="p-6">
                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#E83E70' }}>✓</span>
                    <span>{t('home.insurancePoint1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#E83E70' }}>✓</span>
                    <span>{t('home.insurancePoint2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: '#E83E70' }}>✓</span>
                    <span>{t('home.insurancePoint3')}</span>
                  </li>
                </ul>
                <div
                  className="font-semibold group-hover:translate-x-2 transition-transform inline-flex items-center"
                  style={{ color: '#E83E70' }}
                >
                  {t('home.browseTemporary')}
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* All Properties section - Only show when user has searched */}
      {hasSearched && (
      <>
      {/* Original Category Filter and View Toggle - Keep for All Properties browsing */}
      <div className="border-b border-gray-200">
        <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{t('home.allProperties')}</h3>
              <span className="text-sm text-gray-500">{t('home.availableCount', { count: properties.length })}</span>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`${
                  viewMode === 'grid'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-200'
                }`}
              >
                <Grid className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{t('home.grid')}</span>
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={`${
                  viewMode === 'map'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-200'
                }`}
              >
                <Map className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{t('home.map')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Display Section */}
      <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20 py-4 sm:py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: '#1F5C42' }}></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchProperties} variant="outline">
              {t('common.retry')}
            </Button>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg mb-4">{t('home.noResults')}</p>
            <Button 
              onClick={() => {
                setSearchLocation('');
                setCheckInDate(null);
                setCheckOutDate(null);
                setGuests({ adults: 1, children: 0 });
                setBedrooms(null);
                fetchProperties();
              }}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {properties.map((property) => (
                  <PropertyCard 
                    key={property.id || property._id} 
                    property={property}
                    onClick={() => {
                      // Pass search filters to property page via URL params
                      const params = new URLSearchParams();
                      if (checkInDate) params.append('checkIn', checkInDate.toISOString());
                      if (checkOutDate) params.append('checkOut', checkOutDate.toISOString());
                      if (guests) {
                        params.append('adults', guests.adults || 1);
                        params.append('children', guests.children || 0);
                      }
                      navigate(`/property/${property.id || property._id}?${params.toString()}`);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="h-[700px] rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <PropertyMapView 
                  properties={properties}
                  filters={{
                    minPrice: null,
                    maxPrice: null,
                    category: selectedCategory !== 'all' ? selectedCategory : null,
                    guests: guests.adults + guests.children
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

    </div>
  );
};

export default HomePage;
